// src/context/TimeTrackingContext.js
//
// Tracking state lives here, above the router, so a session survives
// navigating between pages. It used to live inside the tracker page —
// which meant moving to any other page unmounted the component, killed
// the MediaStream, and left an open entry behind.
//
// What still can't survive: a full page reload or closing the tab. A
// MediaStream can't be serialised or resumed, so the browser has to be
// asked for the screen again. That case is handled as a resumable open
// session rather than thrown away — see resumeSession/closeAtLastEvidence
// in the tracker page.
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import {
  collection, doc, addDoc, setDoc, updateDoc, getDoc, serverTimestamp, Timestamp, increment,
} from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../lib/firebase";
import { useAuth } from "./AuthContext";
import { useNotify } from "./NotificationContext";
import { notificationStatus, notifyScreenshot } from "../lib/systemNotify";
import * as Q from "../lib/offlineQueue";
import { tzOf, dayIn, browserTimeZone, validTz } from "../lib/memberTime";

const TimeTrackingContext = createContext(null);
export const useTimeTracking = () => useContext(TimeTrackingContext);

const SETTINGS_DOC = doc(db, "system", "timeTracking");
export const DEFAULT_SETTINGS = { enabled: true, screenshotsEnabled: true, intervalMinutes: 5, blurScreenshots: false, requireEntireScreen: true };


// When the connection is down, Firebase writes don't fail — they wait forever
// for the network. Anything we await has to be given up on after a while so
// the tracker never hangs on it.
const withTimeout = (p, ms) => new Promise((resolve, reject) => {
  const t = setTimeout(() => reject(Object.assign(new Error("Timed out — no connection"), { code: "timeout" })), ms);
  p.then(v => { clearTimeout(t); resolve(v); }, e => { clearTimeout(t); reject(e); });
});
const isOnline = () => typeof navigator === "undefined" || navigator.onLine !== false;
const MAX_AUTO_RETRIES = 10; // per queued screenshot, before it waits for a manual "Upload now"

export function TimeTrackingProvider({ children }) {
  const { currentUser, businessProfile } = useAuth();
  const notify = useNotify();

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [active, setActiveState] = useState(null);   // { id, clockInLocal, billable, rateLabel, hourlyRate, projectName, clientName, special }
  // Mirror of `active` for code that runs from timers and listeners set up at
  // clock-in (auto clock-out when screen sharing stops). Those keep the
  // clock-in render's functions, where `active` was still null — so the
  // session was logged as 0 minutes. They read this ref instead.
  const activeRef = useRef(null);
  const setActive = v => { activeRef.current = v; setActiveState(v); };
  const [elapsed, setElapsed] = useState(0);
  const [shots, setShots] = useState(0);
  const [failedShots, setFailedShots] = useState(0);
  const [busy, setBusy] = useState(false);
  const [online, setOnline] = useState(isOnline());
  const [pendingUploads, setPendingUploads] = useState(0); // screenshots/hours saved on this device, not yet sent
  const [syncing, setSyncing] = useState(false);

  const streamRef = useRef(null);
  const intervalRef = useRef(null);
  const entryRef = useRef(null);
  const entryProjectRef = useRef(null); // project name, for the screenshot notification
  const uploadedRef = useRef(0);        // screenshots of THIS session already on the server
  const offlineSinceRef = useRef(null); // when the connection dropped, while a session is running
  const offlineSecondsRef = useRef(0);  // total time offline in this session
  const syncingRef = useRef(false);
  const beatRef = useRef(null);

  useEffect(() => {
    getDoc(SETTINGS_DOC)
      .then(s => { if (s.exists()) setSettings({ ...DEFAULT_SETTINGS, ...s.data() }); })
      .catch(() => {});
  }, []);

  const stopEverything = useCallback(() => {
    if (intervalRef.current) { clearInterval(intervalRef.current); clearTimeout(intervalRef.current); }
    intervalRef.current = null;
    if (beatRef.current) clearInterval(beatRef.current);
    beatRef.current = null;
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  }, []);

  // Ticking clock
  useEffect(() => {
    if (!active) return;
    const t = setInterval(
      () => setElapsed(Math.floor((Date.now() - new Date(active.clockInLocal).getTime()) / 1000)),
      1000,
    );
    return () => clearInterval(t);
  }, [active]);

  // Warn before a reload/close would orphan the session.
  useEffect(() => {
    if (!active) return;
    const handler = e => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [active]);

  // Uploads one screenshot. Written to be safe to run again after a partial
  // failure: the file and document ids come from the capture time, so a
  // retry never creates a second copy.
  async function uploadShot(shot, { retry = false } = {}) {
    const path = `timeTracking/${shot.userId}/${shot.entryId}/${shot.capturedAt}.jpg`;
    const fileRef = storageRef(storage, path);
    let url = null;
    if (retry) {
      // The file may have gone up on an earlier attempt that died before the
      // database write — storage rules don't allow overwriting, so look first.
      try { url = await getDownloadURL(fileRef); } catch { /* not there yet */ }
    }
    if (!url) {
      await uploadBytes(fileRef, shot.blob, { contentType: "image/jpeg" });
      url = await getDownloadURL(fileRef);
    }
    try {
      await withTimeout(setDoc(doc(db, "screenshots", `${shot.entryId}_${shot.capturedAt}`), {
        entryId: shot.entryId, userId: shot.userId, userName: shot.userName,
        url, storagePath: path,
        // The moment it was TAKEN, not the moment it reached us.
        takenAt: Timestamp.fromMillis(shot.capturedAt),
        ...(shot.late ? { capturedOffline: true } : {}),
      }), 15000);
    } catch (err) {
      // A retry that finds the document already there is denied (these are
      // create-only) — that means an earlier attempt already saved it.
      if (!(retry && err.code === "permission-denied")) throw err;
    }
  }

  async function takeShot(entryId) {
    const capturedAt = Date.now();
    let blob;
    try {
      const { captureFrame } = await import("../lib/screenCapture");
      blob = await captureFrame(streamRef.current, { blur: settings.blurScreenshots });
    } catch (err) {
      // Only a share that has really ended clocks you out; a frame that just
      // couldn't be read this time is skipped, and the next one is tried.
      if (err?.name === "ScreenShareEnded" || streamRef.current?.getVideoTracks?.()[0]?.readyState !== "live") {
        notify.warning("Screen sharing stopped — clocking you out.", { title: "Tracking ended" });
        await clockOut();
      } else {
        console.warn("Screenshot skipped:", err?.message);
      }
      return;
    }
    if (!blob) return;

    const shot = {
      entryId, userId: currentUser.uid,
      userName: businessProfile?.name || businessProfile?.email || "",
      capturedAt, blob, projectName: entryProjectRef.current, attempts: 0,
    };
    const stamp = new Date(capturedAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

    let sent = false;
    if (isOnline()) {
      try {
        await uploadShot(shot);
        sent = true;
      } catch (err) {
        console.error("Screenshot upload failed — keeping it on this device:", err.message);
        // Online but still failing (not just a dropped connection) is worth saying once.
        setFailedShots(n => {
          if (n === 0) notify.warning("Screenshots aren't uploading right now — they're saved on this device and will retry. Your hours are still being tracked.", { title: "Screenshot problem" });
          return n + 1;
        });
      }
    }

    if (sent) {
      uploadedRef.current += 1;
      setShots(n => n + 1);
      // Tell the person a screenshot just went through: a corner toast in the
      // app, plus an OS notification for when they're working in another window.
      notify.toast(stamp, { title: "Screenshot captured", type: "success" });
      notifyScreenshot({ project: shot.projectName });
      // Recorded on the entry too, so an orphaned session can be closed
      // at its last proven moment of activity instead of guessed at.
      updateDoc(doc(db, "timeEntries", entryId), { lastScreenshotAt: new Date(capturedAt).toISOString() }).catch(() => {});
      // A good moment to flush anything saved earlier.
      Q.countPending(currentUser.uid).then(n => { if (n > 0) syncPending(); }).catch(() => {});
      return;
    }

    // No connection (or the upload failed): keep the image on the device.
    try {
      await Q.queueShot(shot);
      setPendingUploads(n => n + 1);
      setShots(n => n + 1);
      notify.toast(`${stamp} · will upload when you're back online`, { title: "Screenshot saved on this device", type: "info", duration: 5000 });
      notifyScreenshot({ project: shot.projectName, offline: true });
    } catch (err) {
      console.error("Couldn't keep the screenshot on this device:", err.message);
      notify.warning("A screenshot couldn't be saved. Your hours are still being tracked.", { title: "Screenshot problem" });
    }
  }

  // Sends whatever was saved while offline: first any clock-out that couldn't
  // be recorded, then the screenshots (oldest first), then fixes the counts.
  // Safe to call any time — it does nothing without a connection or user.
  async function syncPending(opts = {}) {
    if (syncingRef.current || !currentUser || !isOnline()) return;
    // Two open tabs would both send the same queue. Where the browser has Web
    // Locks, let exactly one tab do it; otherwise carry on (the uploads are
    // written to be safe to repeat anyway).
    if (typeof navigator !== "undefined" && navigator.locks?.request) {
      return navigator.locks.request("techbugz-offline-sync", { ifAvailable: true }, async lock => {
        if (lock) await runSync(opts);
      });
    }
    return runSync(opts);
  }

  async function runSync({ force = false } = {}) {
    if (syncingRef.current) return;
    syncingRef.current = true; setSyncing(true);
    let uploaded = 0;
    try {
      for (const op of await Q.pendingOps(currentUser.uid)) {
        if (op.type !== "clockOut") continue;
        try {
          await withTimeout(updateDoc(doc(db, "timeEntries", op.entryId), {
            clockOut: Timestamp.fromMillis(op.clockOutMs), clockOutLocal: new Date(op.clockOutMs).toISOString(),
            durationMinutes: op.durationMinutes, screenshotCount: op.screenshotCount, offlineSeconds: op.offlineSeconds || 0,
          }), 15000);
          await Q.removeOp(op.id);
        } catch (err) {
          console.error("Couldn't send a saved clock-out yet:", err.message);
          if (!isOnline()) break;
        }
      }

      const perEntry = {};
      for (const s of await Q.pendingShots(currentUser.uid)) {
        if (!isOnline()) break;
        if (!force && (s.attempts || 0) >= MAX_AUTO_RETRIES) continue;
        try {
          await uploadShot({ ...s, late: true }, { retry: true });
          await Q.removeShot(s.id);
          uploaded += 1;
          perEntry[s.entryId] = (perEntry[s.entryId] || 0) + 1;
        } catch (err) {
          await Q.updateShot({ ...s, attempts: (s.attempts || 0) + 1, lastError: err.message }).catch(() => {});
          if (!isOnline()) break;
        }
      }
      // These were not counted on the entry when they were taken.
      for (const [entryId, n] of Object.entries(perEntry)) {
        await withTimeout(updateDoc(doc(db, "timeEntries", entryId), { screenshotCount: increment(n) }), 15000).catch(() => {});
      }
      if (uploaded > 0) {
        notify.toast(`${uploaded} screenshot${uploaded === 1 ? "" : "s"} uploaded`, { title: "Back online", type: "success" });
      }
    } catch (err) {
      console.error("Sync failed:", err.message);
    } finally {
      try { setPendingUploads(await Q.countPending(currentUser.uid)); } catch { /* ignore */ }
      syncingRef.current = false; setSyncing(false);
    }
  }

  async function startCapture(entryId) {
    if (!settings.screenshotsEnabled) return;
    const { startScreenShare } = await import("../lib/screenCapture");
    streamRef.current = await startScreenShare({ requireMonitor: settings.requireEntireScreen !== false });
    streamRef.current.getVideoTracks()[0].addEventListener("ended", () => {
      if (entryRef.current) {
        notify.warning("Screen sharing stopped — clocking you out.", { title: "Tracking ended" });
        clockOut();
      }
    });
    takeShot(entryId);
    // Random gaps: each next screenshot comes anywhere from 30 seconds up to
    // the interval (e.g. 3 minutes) after the last — never a fixed beat, never
    // longer than the interval, and sometimes more than one in a few minutes.
    const maxGapMs = Math.max(1, Number(settings.intervalMinutes) || 5) * 60 * 1000;
    const minGapMs = Math.min(30000, maxGapMs / 2);
    const scheduleNext = () => {
      const delay = minGapMs + Math.floor(Math.random() * (maxGapMs - minGapMs));
      intervalRef.current = setTimeout(() => {
        if (!entryRef.current) return;
        takeShot(entryId);
        scheduleNext();
      }, delay);
    };
    scheduleNext();
  }

  // Tells the team report this session is alive right now. A closed laptop
  // or a killed tab stops the beat, which is how an abandoned session is told
  // apart from one still in progress (see lib/liveTime.js).
  function startHeartbeat(entryId) {
    // Skipped while offline — writes would just pile up waiting for the network.
    const beat = () => { if (isOnline()) updateDoc(doc(db, "timeEntries", entryId), { lastSeenAt: new Date().toISOString() }).catch(() => {}); };
    beat();
    beatRef.current = setInterval(beat, 60 * 1000);
  }

  // Screenshot alerts only reach the desktop if the browser has been allowed to
  // show notifications; otherwise the person only sees the in-app toast, which
  // is invisible while they work in another window. Say so once per session.
  function nudgeAboutAlerts() {
    if (!settings.screenshotsEnabled) return;
    const st = notificationStatus();
    if (st === "granted" || st === "unsupported") return;
    notify.toast(
      st === "denied"
        ? "Desktop alerts are blocked for this site, so you'll only see screenshot notices inside this tab. Allow notifications in your browser's site settings."
        : "Turn on desktop alerts (button on the tracker page) to see a notice when a screenshot is taken, even outside this tab.",
      { title: "Desktop alerts are off", type: "warning", duration: 9000 },
    );
  }

  // `project` is required. A normal project can be for any client, so
  // `client` ({ id, name }) says which one THIS session is for; a special
  // project has none.
  async function clockIn({ billable = true, rate = null, note = "", project = null, client = null } = {}) {
    if (!project?.id) {
      notify.warning("Pick a project before you clock in.", { title: "Project needed" });
      throw new Error("Project required");
    }
    if (!project.special && !client?.id) {
      notify.warning("Choose which client this time is for.", { title: "Client needed" });
      throw new Error("Client required");
    }
    if (!isOnline()) {
      notify.warning("You need to be online to clock in. Once you're clocked in you can go offline — the timer keeps running and screenshots upload when you're back.", { title: "You're offline" });
      throw new Error("Offline");
    }
    setBusy(true);
    let createdId = null;
    // The member's own zone decides which day these hours count for. First
    // clock-in with none saved adopts this computer's zone.
    const timeZone = tzOf(businessProfile);
    if (!validTz(businessProfile?.timeZone)) {
      updateDoc(doc(db, "businesses", currentUser.uid), { timeZone: browserTimeZone() }).catch(() => {});
    }
    try {
      const entry = await addDoc(collection(db, "timeEntries"), {
        userId: currentUser.uid,
        userName: businessProfile?.name || businessProfile?.email || "",
        clockIn: serverTimestamp(),
        clockInLocal: new Date().toISOString(),
        clockOut: null,
        date: dayIn(Date.now(), timeZone),
        timeZone,
        screenshotCount: 0,
        screenshotsEnabled: !!settings.screenshotsEnabled,
        billable: !!billable,
        note: note || "",
        // What the hours are for. Names are copied onto the entry so past
        // sessions still read correctly if a project is renamed or archived.
        projectId: project.id,
        projectName: project.name,
        special: !!project.special,
        clientId: project.special ? null : client.id,
        clientName: project.special ? null : client.name || null,
        // Rate is snapshotted at clock-in on purpose: changing someone's
        // rate later must not silently rewrite what past work was worth.
        rateId: rate?.id || null,
        rateLabel: rate?.label || null,
        hourlyRate: rate?.rate ?? null,
      });

      createdId = entry.id;
      entryRef.current = entry.id;
      entryProjectRef.current = project.name;
      uploadedRef.current = 0; offlineSecondsRef.current = 0; offlineSinceRef.current = null;
      setActive({
        id: entry.id, clockInLocal: new Date().toISOString(), billable, rateLabel: rate?.label || null, hourlyRate: rate?.rate ?? null,
        projectName: project.name, clientName: project.special ? null : client.name || null, special: !!project.special,
        timeZone,
      });
      setElapsed(0); setShots(0); setFailedShots(0);

      startHeartbeat(entry.id);
      await startCapture(entry.id);
      notify.success("You're clocked in.", { title: "Tracking started" });
      nudgeAboutAlerts();
      return entry.id;
    } catch (err) {
      stopEverything();
      entryRef.current = null;
      setActive(null);
      // The entry was written before the screen-share prompt, so declining it
      // used to leave a session open forever — the "Unfinished session" you'd
      // then see on the next visit. Close it as abandoned (reports skip these).
      if (createdId) {
        updateDoc(doc(db, "timeEntries", createdId), {
          clockOut: serverTimestamp(), clockOutLocal: new Date().toISOString(),
          durationMinutes: 0, abandoned: true, abandonedReason: "clock-in failed",
        }).catch(() => {});
      }
      notify.error(
        err?.name === "NotAllowedError"
          ? "Screen sharing was declined — it's required while screenshots are on."
          : err?.name === "WrongSurfaceError"
            ? "Only a browser tab or window was shared. Clock in again and, when your browser asks, choose Entire screen — then Share."
            : err.message,
        { title: err?.name === "WrongSurfaceError" ? "Share your entire screen" : "Couldn't clock in" },
      );
      throw err;
    } finally {
      setBusy(false);
    }
  }

  // Re-attaches to an entry that's still open (after a reload). Asks for
  // the screen again, because the old stream is gone for good.
  async function resumeSession(entry) {
    setBusy(true);
    try {
      entryRef.current = entry.id;
      entryProjectRef.current = entry.projectName || null;
      uploadedRef.current = entry.screenshotCount || 0;
      offlineSecondsRef.current = entry.offlineSeconds || 0; offlineSinceRef.current = null;
      setActive({
        id: entry.id, clockInLocal: entry.clockInLocal,
        billable: entry.billable !== false, rateLabel: entry.rateLabel, hourlyRate: entry.hourlyRate,
        projectName: entry.projectName || null, clientName: entry.clientName || null, special: !!entry.special,
      });
      setShots(entry.screenshotCount || 0);
      setFailedShots(0);
      startHeartbeat(entry.id);
      await startCapture(entry.id);
      notify.success("Picked up where you left off.", { title: "Session resumed" });
      nudgeAboutAlerts();
    } catch (err) {
      stopEverything();
      entryRef.current = null;
      setActive(null);
      notify.error(
        err?.name === "NotAllowedError" ? "Screen sharing was declined."
          : err?.name === "WrongSurfaceError" ? "Only a browser tab or window was shared. Try again and choose Entire screen."
          : err.message,
        { title: "Couldn't resume" },
      );
    } finally {
      setBusy(false);
    }
  }

  async function clockOut() {
    const current = activeRef.current || active;
    const id = entryRef.current || current?.id;
    if (!id) return;
    setBusy(true);
    try {
      stopEverything();
      const endMs = Date.now();
      let startedAt = new Date(current?.clockInLocal || 0).getTime();
      if (!startedAt) {
        // Last resort: the start time saved on the session itself.
        try { const snap = await getDoc(doc(db, "timeEntries", id)); startedAt = new Date(snap.data()?.clockInLocal || endMs).getTime(); }
        catch { startedAt = endMs; }
      }
      const minutes = Math.max(0, Math.round((endMs - startedAt) / 60000));
      // If the connection dropped and hasn't come back, count that stretch too.
      if (offlineSinceRef.current) { offlineSecondsRef.current += Math.round((endMs - offlineSinceRef.current) / 1000); offlineSinceRef.current = null; }
      const fields = { durationMinutes: minutes, screenshotCount: uploadedRef.current, offlineSeconds: offlineSecondsRef.current };

      let saved = true;
      try {
        if (!isOnline()) throw new Error("offline");
        await withTimeout(updateDoc(doc(db, "timeEntries", id), {
          clockOut: serverTimestamp(), clockOutLocal: new Date(endMs).toISOString(), ...fields,
        }), 10000);
      } catch {
        // No connection: keep the clock-out on this device. The hours are
        // right (the timer ran locally) and it's sent as soon as we're back.
        saved = false;
        await Q.queueOp({ type: "clockOut", userId: currentUser.uid, entryId: id, clockOutMs: endMs, ...fields });
        setPendingUploads(n => n + 1);
      }

      entryRef.current = null;
      entryProjectRef.current = null;
      setActive(null); setElapsed(0);
      notify.success(
        saved
          ? `Logged ${Math.floor(minutes / 60)}h ${minutes % 60}m.`
          : `${Math.floor(minutes / 60)}h ${minutes % 60}m is saved on this device and will be recorded as soon as you're back online.`,
        { title: saved ? "Clocked out" : "Clocked out offline" },
      );
      return minutes;
    } catch (err) {
      notify.error(err.message, { title: "Couldn't clock out" });
    } finally {
      setBusy(false);
    }
  }

  // ── Connection changes ────────────────────────────────
  // While clocked in, a dropped connection doesn't stop anything: the timer is
  // local, screenshots keep being taken and kept on the device. We just note
  // how long we were offline, and send everything when it's back.
  useEffect(() => {
    const goOffline = () => {
      setOnline(false);
      if (entryRef.current && !offlineSinceRef.current) offlineSinceRef.current = Date.now();
    };
    const goOnline = () => {
      setOnline(true);
      if (offlineSinceRef.current && entryRef.current) {
        offlineSecondsRef.current += Math.round((Date.now() - offlineSinceRef.current) / 1000);
        // Absolute value, so repeating it is harmless.
        updateDoc(doc(db, "timeEntries", entryRef.current), { offlineSeconds: offlineSecondsRef.current, lastSeenAt: new Date().toISOString() }).catch(() => {});
      }
      offlineSinceRef.current = null;
      syncPending();
    };
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => { window.removeEventListener("offline", goOffline); window.removeEventListener("online", goOnline); };
  }, [currentUser]); // eslint-disable-line

  // On load: see what's waiting from a previous visit, and send it if we can.
  // Then keep trying every 45s while anything is still waiting.
  useEffect(() => {
    if (!currentUser) return;
    let alive = true;
    const check = async () => {
      try {
        const n = await Q.countPending(currentUser.uid);
        if (!alive) return;
        setPendingUploads(n);
        if (n > 0 && isOnline()) syncPending();
      } catch { /* ignore */ }
    };
    check();
    const t = setInterval(check, 45000);
    return () => { alive = false; clearInterval(t); };
  }, [currentUser]); // eslint-disable-line

  return (
    <TimeTrackingContext.Provider value={{
      settings, setSettings,
      active, elapsed, shots, failedShots, busy,
      online, pendingUploads, syncing, syncNow: () => syncPending({ force: true }),
      clockIn, clockOut, resumeSession,
      tracking: !!active,
    }}>
      {children}
    </TimeTrackingContext.Provider>
  );
}
